import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type Reach = 'everyone' | 'follows_only' | 'nobody';

const REACH_OPTIONS: { value: Reach; label: string; description: string }[] = [
  {
    value: 'everyone',
    label: 'Everyone',
    description:
      'Default. Anything synced from your connected platforms or RSS feeds reaches your feed.',
  },
  {
    value: 'follows_only',
    label: 'Only accounts I follow',
    description:
      "Reserved for when we add inbound mentions/replies. Today, all platform timelines we sync are already 'follows', so this behaves like Everyone.",
  },
  {
    value: 'nobody',
    label: 'Nobody — hold all for review',
    description:
      'Every newly fetched item lands in the Reviewed Queue. Nothing surfaces in your Curated feed unless you explicitly approve it.',
  },
];

export const FeedSovereigntySettings: React.FC = () => {
  const { user } = useAuth();
  const [reach, setReach] = useState<Reach>('everyone');
  const [threshold, setThreshold] = useState(7);
  const [researchOptIn, setResearchOptIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingReach, setSavingReach] = useState(false);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [savingResearch, setSavingResearch] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    supabase
      .from('profiles')
      .select('reach_setting, toxicity_fence_threshold, research_opt_in')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setReach((data?.reach_setting as Reach) ?? 'everyone');
        setThreshold(data?.toxicity_fence_threshold ?? 7);
        setResearchOptIn(Boolean(data?.research_opt_in));
        setLoading(false);
      });
  }, [user?.id]);

  const updateResearch = async (next: boolean) => {
    if (!user?.id) return;
    setSavingResearch(true);
    setResearchOptIn(next);
    let region: string | null = null;
    try {
      region = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {}
    const patch: Record<string, any> = { research_opt_in: next };
    if (next) {
      patch.research_opt_in_at = new Date().toISOString();
      patch.research_opt_in_region = region;
    }
    const { error } = await supabase.from('profiles').update(patch).eq('id', user.id);
    setSavingResearch(false);
    if (error) {
      toast.error(`Failed: ${error.message}`);
      return;
    }
    toast.success(next ? 'Thanks — you have opted in to research.' : 'Research opt-in turned off.');
  };

  const updateReach = async (next: Reach) => {
    if (!user?.id) return;
    setSavingReach(true);
    setReach(next);
    const { error } = await supabase
      .from('profiles')
      .update({ reach_setting: next })
      .eq('id', user.id);
    setSavingReach(false);
    if (error) {
      toast.error(`Failed: ${error.message}`);
      return;
    }
    toast.success('Reach setting saved');
  };

  const commitThreshold = async (next: number) => {
    if (!user?.id) return;
    setSavingThreshold(true);
    setThreshold(next);
    const { error } = await supabase
      .from('profiles')
      .update({ toxicity_fence_threshold: next })
      .eq('id', user.id);
    setSavingThreshold(false);
    if (error) {
      toast.error(`Failed: ${error.message}`);
      return;
    }
    toast.success(`Toxicity fence set to ${next}/10`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Feed sovereignty</h2>
        <p className="text-sm text-muted-foreground">
          Your attention, your rules. Set who can reach you and where the bar sits for content
          we'll hold for review on your behalf.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Who can reach my feed</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={reach}
            onValueChange={(v) => updateReach(v as Reach)}
            className="space-y-3"
            disabled={loading || savingReach}
          >
            {REACH_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex items-start gap-3 cursor-pointer rounded-md border border-border/60 p-3 hover:bg-muted/30"
              >
                <RadioGroupItem value={opt.value} className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{opt.description}</div>
                </div>
              </label>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Toxicity fence</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Items scored above this threshold by Claude Haiku go to the Reviewed Queue. 0 means
            score nothing as too toxic; 10 means only the most extreme content gets queued.
          </p>
          <div className="flex items-baseline justify-between">
            <Label>Threshold</Label>
            <span className="text-xs text-muted-foreground tabular-nums">{threshold} / 10</span>
          </div>
          <Slider
            min={0}
            max={10}
            step={1}
            value={[threshold]}
            onValueChange={(v) => setThreshold(v[0] ?? 0)}
            onValueCommit={(v) => commitThreshold(v[0] ?? 0)}
            disabled={loading || savingThreshold}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Research opt-in</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex items-start gap-3 cursor-pointer">
            <Switch
              checked={researchOptIn}
              onCheckedChange={updateResearch}
              disabled={loading || savingResearch}
              className="mt-0.5"
            />
            <span className="text-sm">
              <strong>Help improve SocialScoop.</strong> Contribute anonymous behavioral data to
              research on healthier social media habits. Aggregated only — your identity is never
              shared, and any panel with fewer than 10 opted-in users shows nothing at all.
            </span>
          </label>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedSovereigntySettings;
